// HubSpot-style reverse-chronological feed for a contact OR user.
// Consumes admin/contact-timeline. Each event renders as a left-rail
// bullet + a card with kind-specific content.
//
// Props (exactly one of contactId/userId, refreshKey to force reload):
//   contactId?: string
//   userId?: string
//   refreshKey?: number
//
// Shown event kinds:
//   message               — an email we sent (status + opens/clicks pills)
//   activity              — fine-grained Postmark webhook event (open/click/bounce)
//   enrolled              — entered an outreach journey
//   enrollment_completed  — finished a journey
//   enrollment_exited     — bailed from a journey
//   subscription          — Stripe tier change

import { useEffect, useState } from 'react';
import { formatDistance } from 'date-fns';
import client from '../lib/feathers';

const STATUS_PILL = {
  queued: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  delivered: 'bg-blue-100 text-blue-800',
  opened: 'bg-green-100 text-green-800',
  clicked: 'bg-purple-100 text-purple-800',
  bounced: 'bg-red-100 text-red-800',
  spam: 'bg-red-100 text-red-800',
  unsubscribed: 'bg-gray-200 text-gray-700',
};

const ACTIVITY_DOT = {
  sent: 'bg-blue-500',
  delivered: 'bg-blue-500',
  opened: 'bg-green-500',
  clicked: 'bg-purple-500',
  bounced: 'bg-red-500',
  spam_complaint: 'bg-red-500',
  unsubscribed: 'bg-gray-500',
};

const KIND_DOT = {
  message: 'bg-primary',
  activity: 'bg-gray-400',
  enrolled: 'bg-indigo-500',
  enrollment_completed: 'bg-indigo-700',
  enrollment_exited: 'bg-yellow-600',
  subscription: 'bg-emerald-600',
};

const ago = (d) => (d ? formatDistance(new Date(d), new Date(), { addSuffix: true }) : '—');

function MessageEvent({ event }) {
  const d = event.data;
  const pill = STATUS_PILL[d.status] || 'bg-yellow-100 text-yellow-800';
  const sourceLabel = d.source === 'campaign' && d.campaign
    ? `Campaign: ${d.campaign.name}`
    : d.source === 'journey' && d.journey
      ? `Journey: ${d.journey.name}`
      : 'Direct send';
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">
          {d.subject || sourceLabel}
        </p>
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pill}`}>
          {d.status}
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {sourceLabel}
        {d.openCount > 0 && <> · {d.openCount} open{d.openCount === 1 ? '' : 's'}</>}
        {d.firstClickedAt && <> · clicked {ago(d.firstClickedAt)}</>}
        {d.bounceType && <> · bounce: {d.bounceType}</>}
      </p>
      {d.errorMessage && (
        <p className="text-xs text-red-600 mt-1">{d.errorMessage}</p>
      )}
    </div>
  );
}

function ActivityEvent({ event }) {
  const d = event.data;
  const label = {
    sent: 'Sent',
    delivered: 'Delivered',
    opened: 'Opened',
    clicked: 'Clicked a link',
    bounced: 'Bounced',
    spam_complaint: 'Marked as spam',
    unsubscribed: 'Unsubscribed',
  }[d.event] || d.event;
  // The webhook stores detail.originalLink / clickLocation / userAgent / os /
  // platform (postmark-outreach-webhook.service.js). This read detail.url and
  // detail.ua, keys nothing has ever written, so every event rendered as a
  // bare label and the clicked URL, which we capture in full, was invisible.
  const detail = d.detail || {};
  // Gmail fetches the tracking pixel server-side, so an open through it always
  // reports Google's fixed proxy signature (Windows NT 5.1 / Firefox 11) rather
  // than the reader's real device. The OPEN is genuine (this is what every
  // real Gmail open looks like), but rendering "Desktop, Windows" from it
  // states a device we simply don't know. Name the proxy instead.
  const viaProxy = /GoogleImageProxy|YahooMailProxy|ProxyAgent/i.test(detail.userAgent || '');
  const client = viaProxy
    ? 'opened via Gmail (device not reported)'
    : [detail.platform, detail.os?.Name || detail.os?.Family].filter(Boolean).join(' · ');
  // A click's URL is the point of the row; for everything else the device is
  // the only useful thing in there (the raw UA is a fallback for old rows
  // written before os/platform were captured).
  const detailLine = (d.event === 'clicked'
    ? [detail.originalLink, client]
    : [client || detail.userAgent]
  ).filter(Boolean).join(' · ');
  return (
    <div>
      <p className="text-sm text-gray-800">{label}</p>
      {detailLine && (
        <p className="text-xs text-gray-500 mt-0.5 truncate" title={detailLine}>{detailLine}</p>
      )}
    </div>
  );
}

function EnrolledEvent({ event }) {
  const j = event.data.journey;
  return (
    <p className="text-sm text-gray-800">
      Enrolled in journey <span className="font-medium">{j?.name || 'journey'}</span>
      {event.data.currentStepIndex > 0 && (
        <span className="text-gray-500"> · at step {event.data.currentStepIndex + 1}</span>
      )}
    </p>
  );
}

function EnrollmentCompletedEvent({ event }) {
  const j = event.data.journey;
  return (
    <p className="text-sm text-gray-800">
      Completed journey <span className="font-medium">{j?.name || 'journey'}</span>
    </p>
  );
}

function EnrollmentExitedEvent({ event }) {
  const j = event.data.journey;
  return (
    <p className="text-sm text-gray-800">
      Exited journey <span className="font-medium">{j?.name || 'journey'}</span>
      {event.data.exitReason && <span className="text-gray-500"> · {event.data.exitReason}</span>}
    </p>
  );
}

function SubscriptionEvent({ event }) {
  const d = event.data;
  return (
    <div>
      <p className="text-sm text-gray-800">{d.summary}</p>
      <p className="text-xs text-gray-500 mt-0.5">
        {d.previousAccessLevel || '—'} → {d.newAccessLevel || '—'}
        {d.stripeSubscriptionStatus && <> · {d.stripeSubscriptionStatus}</>}
      </p>
    </div>
  );
}

function eventBody(event) {
  switch (event.kind) {
    case 'message': return <MessageEvent event={event} />;
    case 'activity': return <ActivityEvent event={event} />;
    case 'enrolled': return <EnrolledEvent event={event} />;
    case 'enrollment_completed': return <EnrollmentCompletedEvent event={event} />;
    case 'enrollment_exited': return <EnrollmentExitedEvent event={event} />;
    case 'subscription': return <SubscriptionEvent event={event} />;
    default: return <p className="text-sm text-gray-500">{event.kind}</p>;
  }
}

export default function ContactTimeline({ contactId, userId, refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = { $limit: 100 };
    if (contactId) query.contactId = contactId;
    else if (userId) query.userId = userId;
    else {
      setLoading(false);
      return () => {};
    }
    client.service('admin/contact-timeline').find({ query })
      .then((res) => {
        if (cancelled) return;
        setEvents(res.data || []);
        setContacts(res.contacts || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load timeline');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactId, userId, refreshKey]);

  if (loading) {
    return <p className="text-gray-500 text-center py-6">Loading timeline…</p>;
  }
  if (error) {
    return <p className="text-red-600 text-center py-6">{error}</p>;
  }
  if (events.length === 0) {
    return <p className="text-gray-500 text-center py-6">No activity yet.</p>;
  }

  return (
    <div className="relative">
      {contacts.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {contacts.map((c) => {
            const cls = c.status === 'active' ? 'bg-green-100 text-green-800'
              : c.status === 'unsubscribed' ? 'bg-gray-200 text-gray-700'
                : c.status === 'bounced' ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800';
            return (
              <span key={c.id} className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${cls}`}>
                {c.email} · {c.status}
              </span>
            );
          })}
        </div>
      )}
      <ol className="relative border-l border-gray-200 pl-6 space-y-5">
        {events.map((event, idx) => {
          const dotCls = event.kind === 'activity'
            ? (ACTIVITY_DOT[event.data.event] || 'bg-gray-400')
            : (KIND_DOT[event.kind] || 'bg-gray-400');
          const key = `${event.kind}-${event.data.id || idx}-${event.occurredAt}`;
          return (
            <li key={key} className="relative">
              <span className={`absolute -left-[31px] top-1.5 h-3 w-3 rounded-full ring-4 ring-white ${dotCls}`} />
              <div className="text-xs text-gray-500 mb-1">{ago(event.occurredAt)}</div>
              {eventBody(event)}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
